import { useEffect, useState } from "react";
import { CalendarDays } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import api from "@/lib/api";
import { euro } from "@/lib/format";
import { fmtDay } from "@/lib/cantieri";
import { TIPI } from "@/components/cantieri/MovimentiDialog";

export const meseLabel = (m) => {
  if (!m) return "Da sempre";
  const [y, mo] = m.split("-").map(Number);
  const s = new Date(y, mo - 1, 1).toLocaleDateString("it-IT", { month: "long", year: "numeric" });
  return s.charAt(0).toUpperCase() + s.slice(1);
};

export const ultimiMesi = (n = 12) => {
  const d = new Date();
  return Array.from({ length: n }, (_, i) => {
    const x = new Date(d.getFullYear(), d.getMonth() - i, 1);
    return `${x.getFullYear()}-${String(x.getMonth() + 1).padStart(2, "0")}`;
  });
};

const Tot = ({ label, value, strong, testid }) => (
  <div className="rounded-lg bg-secondary/50 px-3 py-2">
    <div className="text-[11px] uppercase tracking-wider font-semibold text-muted-foreground">{label}</div>
    <div className={`font-heading tabular-nums ${strong ? "text-lg font-extrabold text-primary" : "font-bold"}`} data-testid={testid}>{value}</div>
  </div>
);

// Riepilogo per il titolare: giorno per giorno dove ha lavorato l'operaio, quanto ha maturato e gli anticipi del periodo.
export const RiepilogoOperaioDialog = ({ operaio, mese, s, onClose }) => {
  const [d, setD] = useState(null);
  useEffect(() => {
    api.get(`/operai/${operaio.id}/giorni`, { params: { mese: mese || undefined } }).then((r) => setD(r.data)).catch(() => setD({ righe: [], movimenti: [] }));
  }, [operaio.id, mese]);
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl max-h-[92vh] overflow-y-auto [&>*]:min-w-0" data-testid="riepilogo-operaio-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading">{operaio.nome} · {meseLabel(mese)}</DialogTitle>
          <DialogDescription>Solo per te: ore, cantieri e soldi del periodo. «Da pagare in tutto» tiene conto di tutto, da sempre.</DialogDescription>
        </DialogHeader>
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
          <Tot label="Giorni" value={s?.giorni ?? 0} testid="riepilogo-days" />
          <Tot label="Ore" value={`${(s?.ore || 0).toLocaleString("it-IT")} h`} testid="riepilogo-hours" />
          <Tot label="Maturato" value={euro(s?.maturato || 0)} testid="riepilogo-earned" />
          <Tot label="Anticipi" value={euro(s?.anticipi || 0)} testid="riepilogo-advances" />
          <Tot label="Rimborsi" value={euro(s?.rimborsi || 0)} testid="riepilogo-refunds" />
          <Tot label="Da pagare in tutto" value={euro(s?.da_pagare_totale || 0)} strong testid="riepilogo-due-total" />
        </div>
        {mese && <div className="text-sm text-muted-foreground" data-testid="riepilogo-month-balance">Saldo del mese (maturato + rimborsi − anticipi): <b className="text-foreground">{euro(s?.da_pagare || 0)}</b></div>}
        <div className="rounded-xl border border-border divide-y divide-border" data-testid="riepilogo-days-list">
          {(d?.righe || []).map((r) => (
            <div key={r.id} className="flex items-center gap-3 px-3 py-2 text-sm" data-testid={`riepilogo-day-${r.id}`}>
              <CalendarDays className="w-4 h-4 text-muted-foreground shrink-0" />
              <span className="w-24 shrink-0">{fmtDay(r.data)}</span>
              <span className="flex-1 min-w-0 truncate font-medium">{r.cantiere}</span>
              <span className="tabular-nums w-14 text-right">{r.ore.toLocaleString("it-IT")} h</span>
              <span className="tabular-nums w-24 text-right text-muted-foreground">{euro(r.importo)}</span>
            </div>
          ))}
          {d && !d.righe.length && <div className="p-6 text-center text-sm text-muted-foreground">Nessuna ora registrata in questo periodo.</div>}
          {!d && <div className="p-6 text-center text-sm text-muted-foreground">Carico…</div>}
        </div>
        {d?.movimenti?.length > 0 && (
          <div className="rounded-xl border border-border divide-y divide-border" data-testid="riepilogo-movements">
            {d.movimenti.map((m) => (
              <div key={m.id} className="flex items-center gap-3 px-3 py-2 text-sm">
                <span className="w-24 shrink-0 text-muted-foreground">{fmtDay(m.data)}</span>
                <span className="flex-1 min-w-0 truncate">{TIPI[m.tipo]}{m.note ? ` · ${m.note}` : ""}</span>
                <span className={`font-semibold tabular-nums ${m.tipo === "anticipo" ? "text-rose-600 dark:text-rose-400" : "text-emerald-600 dark:text-emerald-400"}`}>{m.tipo === "anticipo" ? "−" : "+"}{euro(m.importo)}</span>
              </div>
            ))}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default RiepilogoOperaioDialog;
